import { useParams } from "react-router-dom";
import api from "../../globals/axios";
import { useState, useEffect } from "react";
import LoaderContainer from "../../components/LoaderContainer";
import CustomAppBar from "../../components/AppBar";
import {
  CustomLabel,
  CustomTitle,
  CustomValue,
} from "../../components/Custom/CustomTypography";
import { useTranslation } from "react-i18next";
import {
  Typography,
  Container,
  Box,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import CustomDetailedList from "../../components/Custom/CustomDetailedList";
import CustomButton from "../../components/Custom/CustomButton";
import { useNavigate } from "react-router-dom";
import AssignProgramDialog from "../pop_manager/AssignProgramDialog";
import noteIcon from "../../assets/icons/note-icon.png";
import { useAlert } from "../../components/Alert";

const RecommendedUsersCard = ({ recommendedUsers, t }) => {
  return (
    <Card sx={{ mt: 2, p: 1, boxShadow: 3 }}>
      <CardContent>
        <Typography variant="h6" fontWeight="bold">
          {t("Recommended Users")}
        </Typography>

        {recommendedUsers.length > 0 ? (
          <>
            <Typography variant="body2" color="text.secondary">
              {t("Total")}: {recommendedUsers.length}
            </Typography>
            <Typography variant="body1" mt={1}>
              {recommendedUsers
                .slice(0, 10)
                .map((user) => `${user.full_name}`)
                .join(", ")}
              {recommendedUsers.length > 10 && " ......."}
            </Typography>
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
            mt={1}
            gap={1}
          >
            <img src={noteIcon} alt="note-icon" height={40} width={40} />
            <Typography variant="body1">{t("No users recommended")}</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const ProgramDetails = () => {
  const { programId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const { t } = useTranslation();
  const [programInputId, setProgramInputId] = useState(0);
  const [programType, setProgramType] = useState("");
  const [recommendedUsers, setRecommendedUsers] = useState([]);
  const navigate = useNavigate();
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const { showAlert } = useAlert();

  const getUserRole = () => {
    const userStr = localStorage.getItem("auth");
    if (!userStr) return null;

    try {
      const userData = JSON.parse(userStr);
      console.log(userData?.role);
      return userData?.role;
    } catch (error) {
      console.error("Error parsing user data:", error);
      return null;
    }
  };

  // Get user role
  const userRole = getUserRole();

  const handleOpenAssignDialog = () => {
    setIsAssignDialogOpen(true);
  };

  const handleAssignComplete = () => {
    loadProgramDetails();
    loadRecommendedUsers(programId);
  };

  const loadProgramDetails = async () => {
    setLoading(true);
    try {
      const result = await api.get(
        `/programs/all-programs/?program_id=${programId}`
      );
      setData(result.data);
      setProgramInputId(result.data.program_inputs?.[0].id);
      setProgramType(result.data.program_type);
    } catch (err) {
      console.log(
        "Error: " +
          (err.response?.data?.non_field_errors?.[0] || "Something went wrong")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = () => {
    navigate(`/programs/${programInputId}/add-tasks`, {
      state: {
        program_type: programType,
        program_id: programId,
      },
    });
  };

  const loadRecommendedUsers = async (programId) => {
    try {
      let userLevel;
      if (userRole === "POP_MANAGER") {
        userLevel = "AREA_MANAGER";
      } else if (userRole === "AREA_MANAGER") {
        userLevel = "VLE";
      } else if (userRole === "VLE") {
        userLevel = "FARMER";
      }

      const response = await api.get(
        `/programs/get-program-recommended-users/`,
        {
          params: {
            program_id: programId,
            user_level: userLevel,
          },
        }
      );
      setRecommendedUsers(response.data.users);
    } catch (error) {
      console.error("Error fetching recommended users:", error);
      showAlert("error", "Failed to fetch recommended users.");
    }
  };

  const programDetails = data?.program_inputs?.[0]
    ? [
        {
          label: t("Program Name"),
          value: data.name,
        },
        {
          label: t("Crops"),
          value: data.program_inputs[0].commodity?.name,
        },
        {
          label: t("Geographies"),
          value: data.program_inputs[0].states?.name,
        },
        {
          label: t("Cost to farmer"),
          value: `${data.program_inputs[0].farmer_cost} Rs. per acre`,
        },
        {
          label: t("GHG"),
          value: `${data.program_inputs[0].ghg} per acre`,
        },
        {
          label: t("Water"),
          value: `${data.program_inputs[0].water} per acre`,
        },
        {
          label: t("Program end date"),
          value: data.program_inputs[0].program_end_date,
        },
      ]
    : [];

  useEffect(() => {
    const fetchData = async () => {
      try {
        await loadProgramDetails();
        await loadRecommendedUsers(programId);
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };

    if (programId) {
      fetchData();
    }
  }, [programId]);

  return (
    <>
      <LoaderContainer loading={loading}>
        <CustomAppBar />
        <Container maxWidth="xs" mt={2}>
          <CustomTitle>{t(`${data?.name}`)}</CustomTitle>
          <Box mt={2}>
            <CustomLabel>{t("Program Details -")}</CustomLabel>

            <Box mt={2}>
              {programDetails.map(({ label, value }, index) => (
                <Grid container spacing={2} key={index}>
                  <Grid item xs={6}>
                    <CustomLabel>
                      <Typography variant="body1" fontWeight="bold">
                        {label}:
                      </Typography>
                    </CustomLabel>
                  </Grid>
                  <Grid item xs={6}>
                    <CustomValue>
                      <Typography variant="body1">{value}</Typography>
                    </CustomValue>
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Box>
          <RecommendedUsersCard recommendedUsers={recommendedUsers} t={t} />

          <CustomButton onClick={handleOpenAssignDialog} hideIcon>
            {userRole === "POP_MANAGER" && t("Recommend to Area Managers")}
            {userRole === "AREA_MANAGER" && t("Recommend to VLEs")}
            {userRole === "VLE" && t("Recommend to Farmers")}
          </CustomButton>

          {data?.program_inputs?.[0]?.program_tasks && (
            <Box mt={2}>
              <CustomLabel>{t("Program Tasks -")}</CustomLabel>

              <Box mt={2}>
                {data.program_inputs[0].program_tasks.map((programTask) => (
                  <CustomDetailedList
                    key={programTask.id}
                    id={programTask.id}
                    data={[
                      {
                        label: t("Activity Name"),
                        value: programTask.name,
                      },
                      {
                        label: t("Program start date"),
                        value: programTask.start_date,
                      },
                      {
                        label: t("Program end date"),
                        value: programTask.due_date,
                      },
                    ]}
                    buttonList={[]}
                  />
                ))}
              </Box>
            </Box>
          )}
          {userRole === "POP_MANAGER" && (
            <CustomButton onClick={handleAddTask}>{t("Add Task")}</CustomButton>
          )}
        </Container>
      </LoaderContainer>
      <AssignProgramDialog
        open={isAssignDialogOpen}
        onClose={() => setIsAssignDialogOpen(false)}
        programId={programId}
        onAssign={handleAssignComplete}
        assignedManagers={recommendedUsers.map((user) => user.id)}
        userRole={userRole}
      />
    </>
  );
};

export default ProgramDetails;
